import { Box, Button, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { Icon, Tooltip } from "@familyzone/component-library";
import React, { useEffect, useMemo, useState } from "react";
import { FocusLibraryDrawer } from "../FocusLibraryDrawer";
import { EditingFocus } from "../FocusLibraryDrawer/FocusPage";
import { FocusDTO } from "js/api/focus";
import { getSignaturesForFocusResourcePicker, transformFocusDtoToEditingFocus } from "../FocusLibraryDrawer/FocusResourcePicker/utils";
import useStore from "stores/index";
import { colours } from "utils/themeUtils";
import { useFocusStore } from "stores/v2/focusStore";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { FeatureFlag } from "@/js/api/features";

interface Props {
	focus: EditingFocus;
	isUpdatingFocus: boolean;
	classIsActive: boolean;
	endFocus: (focusId: string) => void;
	bubbleUpdatedFocus: (focus: EditingFocus) => void;
}

export function AppliedFocus(props: Props): JSX.Element {
	const focusLibraryDrawer = useDisclosure();

	const focusStore = useFocusStore();
	const userSessionStore = useUserSessionStore();
	const featureFlagsQuery = useFeatureFlagsQuery(userSessionStore.active_device as string);

	const [appliedFocus, setAppliedFocus] = useState<EditingFocus>(props.focus);

	// Stops the drawer being re-rendered when appliedFocus is updated
	// unless the actual value changes. This prevents the drawer from being
	// re-rendered every time the class screenshots are updated.
	const memoisedAppliedFocus: EditingFocus = useMemo(
		() => ({
			...appliedFocus,
			active: true,
		}),
		[appliedFocus]
	);

	function handleClickEditFocus() {
		focusStore.setOpenedDirectlyToEdit(true);
		focusLibraryDrawer.onOpen();
	}

	function handleClickEndFocus() {
		props.endFocus(appliedFocus.id);
	}

	useEffect(() => {
		setAppliedFocus(props.focus);
	}, [props.focus]);

	const store = useStore();
	const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(store.signatures);

	return (
		<>
			<Flex background={props.classIsActive ? colours.blue[600] : colours.navy[800]} mr={10} rounded={"xs"} color={"white"} px={2}>
				<Flex alignItems={"center"} mx={15} gap={10}>
					<Icon icon={"fa-arrows-to-eye"} />
					<Text fontWeight={"medium"} data-testid="applied-focus-label">
						In Focus{appliedFocus.name && `: ${appliedFocus.name}`}
					</Text>
					{featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && appliedFocus.tabsLocked && (
						<Flex background={"#0075DB"} px={8} py={2} rounded={"sm"} alignItems={"center"} gap={5} fontSize={"xs"} fontWeight={"medium"}>
							<Icon icon={"fa-lock"} />
							<Text>LOCKED</Text>
						</Flex>
					)}
				</Flex>
				<Box borderLeft={"1px solid rgba(255, 255, 255, 0.5)"}></Box>
				<Tooltip label="Click to edit focus resources" variant="dark">
					<Button
						variant={"unstyled"}
						cursor={"pointer"}
						background={"inherit"}
						color={"inherit"}
						onClick={handleClickEditFocus}
						size={"sm"}
						mx={2}
						aria-label="Click to edit focus resources"
						data-testid="edit-applied-focus-button"
					>
						<Icon icon={"fa-pencil"} fontSize={"sm"} />
					</Button>
				</Tooltip>
				<Box borderLeft={"1px solid rgba(255, 255, 255, 0.5)"}></Box>
				<Tooltip label="Click to end focus" variant="dark">
					<Button
						variant={"unstyled"}
						cursor={"pointer"}
						background={"inherit"}
						color={"inherit"}
						onClick={handleClickEndFocus}
						size={"sm"}
						mx={2}
						isDisabled={props.isUpdatingFocus}
						_disabled={{ cursor: "not-allowed", background: "inherit", color: "inherit" }}
						aria-label="Click to end focus"
						data-testid="end-applied-focus-button"
					>
						<Icon icon={"fa-times"} />
					</Button>
				</Tooltip>
			</Flex>
			<FocusLibraryDrawer
				isOpen={focusLibraryDrawer.isOpen}
				closeDrawer={focusLibraryDrawer.onClose}
				focus={memoisedAppliedFocus}
				bubbleAppliedOrUpdatedFocus={(appliedFocus: FocusDTO) => {
					const updatedFocus = transformFocusDtoToEditingFocus(appliedFocus, allSignatureResourceOptions);
					setAppliedFocus(updatedFocus);
					props.bubbleUpdatedFocus(updatedFocus);
				}}
			/>
		</>
	);
}
